import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useActiveTeam } from '../lib/queries/useActiveTeam';
import { useTeamSessions } from '../lib/queries/useTeamSessions';
import { BrainOrb, Card, EmptyState, Skeleton } from '../components/primitives';
import './page-shell.css';

/**
 * /tactics — landing page for Josh's tactics board.
 *
 * The board itself is per-session (it needs a session id for the
 * persistKey). This page lists the team's sessions and lets the coach
 * jump straight to 2D / 3D for any of them. The most-recent session
 * auto-opens after a short delay if the coach doesn't pick something
 * else — same UX as the calendar's "latest" default.
 */
export function Tactics(): React.JSX.Element {
  const navigate = useNavigate();
  const { team, loading: teamLoading } = useActiveTeam();
  const { rows: sessions, state } = useTeamSessions(team?.id, teamLoading, {
    order: 'desc',
    limit: 20,
  });
  const [autoOpen, setAutoOpen] = useState(true);

  // Auto-open the most recent session's 2D board after a brief moment
  // unless the coach explicitly picks one. This makes the sidebar
  // Tactics item feel like a single click → board.
  useEffect(() => {
    if (!autoOpen || state !== 'ok' || sessions.length === 0) return;
    const latest = sessions[0];
    const t = window.setTimeout(() => {
      navigate(`/sessions/${latest.id}/2d`);
    }, 600);
    return () => window.clearTimeout(t);
  }, [autoOpen, sessions, state, navigate]);

  return (
    <main className="main page" style={{ maxWidth: 880, margin: '0 auto', padding: 24 }}>
      <header className="page-head">
        <BrainOrb size="lg" ariaLabel="Brain" />
        <div style={{ flex: 1 }}>
          <span className="page-eyebrow">Tactics board</span>
          <h1 className="page-title">Tactics</h1>
          <p className="page-sub">Jump straight into the 2D / 3D board for any session.</p>
        </div>
      </header>

      {state === 'loading' && (
        <Card variant="opaque"><Skeleton variant="text" width="70%" height={16} /></Card>
      )}

      {state === 'empty' && (
        <EmptyState
          glyph="⚽"
          title="No sessions yet"
          body="The tactics board attaches to a session — plan one first."
          cta={{ label: 'Plan a session', onClick: () => navigate('/sessions/new') }}
        />
      )}

      {state === 'ok' && sessions.length > 0 && (
        <>
          <p style={{ fontSize: 12, opacity: 0.7, margin: '0 0 12px' }}>
            Opening the latest session in 600ms — click anything to take over.
            <button
              type="button"
              onClick={() => setAutoOpen(false)}
              style={{ marginLeft: 8, background: 'transparent', border: '1px solid rgba(255,255,255,0.25)', color: 'inherit', borderRadius: 4, padding: '2px 8px', fontSize: 11, cursor: 'pointer' }}
            >
              Cancel auto-open
            </button>
          </p>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {sessions.map((s) => (
              <li key={s.id}>
                <Card variant="opaque">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: 4 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600 }}>{s.title ?? 'Untitled session'}</div>
                      <div style={{ fontSize: 12, opacity: 0.7 }}>
                        {new Date(s.date).toLocaleDateString()}{s.duration_mins ? ` · ${s.duration_mins} min` : ''}
                      </div>
                    </div>
                    <Link
                      to={`/sessions/${s.id}/2d`}
                      onClick={() => setAutoOpen(false)}
                      style={{ padding: '6px 14px', background: 'var(--brand, #2980B9)', color: '#fff', textDecoration: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600 }}
                    >
                      2D
                    </Link>
                    <Link
                      to={`/sessions/${s.id}/3d`}
                      onClick={() => setAutoOpen(false)}
                      style={{ padding: '6px 14px', background: 'transparent', color: 'inherit', border: '1px solid rgba(255,255,255,0.25)', textDecoration: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600 }}
                    >
                      3D
                    </Link>
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        </>
      )}
    </main>
  );
}
